import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import { BUILDING_STATUSES, STATUS_OB_ALL, USER_ROLES } from "../../../../../../services/constants";
import { PMFlow } from "../../../flows";
import { theme } from "../../../../../../theme";
import { COLORS } from "../../../../../../theme/colors";
import { OnboardingProcess } from "./onboardingProcess";
import { Select } from "../../../../../../components/base/Select";
import { MessageAlert } from "../../../../../../components/alerts/MessageAlert";
import { StyledTableCell, StyledTableRow } from "../../../../../../components/base";
import { setSettings } from "../../../../../../redux/auth/actions";
import endPoints from "../../../../../../services/apiEndPoints";
import { postData, putData } from "../../../../../../services";

const useStyles = makeStyles(() => ({
  root: {
    paddingBottom: 30,
  },
  filterRoot: {
    fontSize: 12,
    color: COLORS.white,
    fontWeight: "bold",
    letterSpacing: 0.1,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  tableContainer: {
    borderRadius: 8,
    maxHeight: 556,
    border: `1px solid ${COLORS.darks[0][10]}`,
    boxShadow: "none",
  },
  kickoffStatus: {
    color: COLORS.white,
    fontWeight: "bold",
  },
  tableRow: {
    backgroundColor: COLORS.white,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.darks[9],
    },
  },
  dots: {
    "& .MuiPaper-elevation8": {
      boxShadow: "1px 1px 1px lightgray",
    },
    "& .MuiListItem-button:hover": {
      backgroundColor: COLORS.white,
    },
  },
}));

export const BuildingListView = ({ buildings, statusCount, onReload }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { cluster } = useSelector((state) => state.selectCSC);
  const isSuperPM = cluster?.user_role === USER_ROLES.super_pm;

  const [statusFilter, setStatusFilter] = useState(STATUS_OB_ALL.key);
  const [selectedBuilding, setSelectedBuilding] = useState();
  const [buildingForInvite, setBuildingForInvite] = useState();
  const [building, setBuilding] = useState();
  const [inviteMsg, setInviteMsg] = useState("");
  const [isBopInvite, setBopInvite] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setTimeout(() => {
      setBopInvite(false);
    }, 3000);
  }, [isBopInvite]);

  const filteredBuildings = useMemo(() => {
    return buildings.filter(
      (b) =>
        b.onboarding_status === statusFilter.toUpperCase() || statusFilter === STATUS_OB_ALL.key
    );
  }, [statusFilter, buildings]);

  const handleOnClose = () => {
    putData("auth/user-settings/", (data) => dispatch(setSettings(data)), {
      show_pm_onboarding_notice: false,
    });
  };

  useEffect(() => {
    if (buildingForInvite?.id !== undefined && filteredBuildings.length) {
      const buildingData = filteredBuildings.find(
        (building) => building.id === buildingForInvite.id
      );
      setBuilding(buildingData);
    }
  }, [buildingForInvite, filteredBuildings]);

  const handleInviteBuilding = (e, building) => {
    e.stopPropagation();
    setBuildingForInvite(building);
  };

  const resendInvite = () => {
    setAnchorEl(null);
    postData(endPoints.RESEND_BOP_INVITE.replace("{id}", building?.id), (data) => {
      setBopInvite(true);
      setInviteMsg(data);
      setBuildingForInvite();
    }).catch((e) => console.log(e));
  };

  return (
    <Box className={classes.root}>
      <OnboardingProcess buildings={buildings} statusCount={statusCount} />
      <Box display="flex" alignItems="center" mt={3} mb={2}>
        <Typography>Filter by status:</Typography>
        <Box bgcolor={COLORS.darks[0][25]} borderRadius={6} fontSize={12} px={1} ml={1.5}>
          <Select
            rootStyle={classes.filterRoot}
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.keys(BUILDING_STATUSES).concat(STATUS_OB_ALL.key)}
            optionNames={Object.values(BUILDING_STATUSES)
              .map((v) => v.name)
              .concat(STATUS_OB_ALL.name)}
          />
        </Box>
      </Box>
      {filteredBuildings.length > 0 && (
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <StyledTableRow style={{ borderBottom: `1px solid ${COLORS.darks[0][10]}` }}>
                <StyledTableCell>Status</StyledTableCell>
                {isSuperPM && (
                  <>
                    <StyledTableCell>Cluster</StyledTableCell>
                    <StyledTableCell>PM name</StyledTableCell>
                  </>
                )}
                <StyledTableCell>Building Name</StyledTableCell>
                <StyledTableCell>Location</StyledTableCell>
                <StyledTableCell>{user.unit_system}</StyledTableCell>
                <StyledTableCell>Units</StyledTableCell>
                <StyledTableCell></StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {filteredBuildings.map((building, i) => (
                <StyledTableRow
                  className={classes.tableRow}
                  key={i}
                  onClick={() => setSelectedBuilding(building)}
                >
                  <StyledTableCell>
                    <Box
                      bgcolor={
                        BUILDING_STATUSES[building.onboarding_status?.toLowerCase()]?.color ||
                        "transparent"
                      }
                      width={100}
                      borderRadius={4}
                      px={0.3}
                      py={0.2}
                    >
                      <Typography className={classes.kickoffStatus} align="center" variant="body2">
                        {BUILDING_STATUSES[building.onboarding_status?.toLowerCase()]?.name}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  {isSuperPM && (
                    <>
                      <StyledTableCell>{building.manage_building?.cluster_name}</StyledTableCell>
                      <StyledTableCell>{building.manage_building?.pm}</StyledTableCell>
                    </>
                  )}
                  <StyledTableCell>{building.name}</StyledTableCell>
                  <StyledTableCell>{building.address}</StyledTableCell>
                  <StyledTableCell>{building.total_square}</StyledTableCell>
                  <StyledTableCell>{building.total_unit}</StyledTableCell>
                  <StyledTableCell onClick={(e) => handleInviteBuilding(e, building)}>
                    <IconButton
                      aria-label="more"
                      aria-controls="long-menu"
                      aria-haspopup="true"
                      onClick={(event) => setAnchorEl(event.currentTarget)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      id="menu"
                      anchorEl={anchorEl}
                      keepMounted
                      open={open}
                      onClose={() => setAnchorEl(null)}
                      className={classes.dots}
                      getContentAnchorEl={null}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <MenuItem
                        onClick={() => resendInvite()}
                        disabled={
                          buildingForInvite?.onboarding_status === "READY" ||
                          buildingForInvite?.onboarding_status === null
                        }
                      >
                        Resend invitation
                      </MenuItem>
                    </Menu>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {user?.settings?.show_pm_onboarding_notice && (
        <MessageAlert
          topOffset={150}
          text="Click a building in the list below to start the onboarding process"
          textStyle={{ fontStyle: "italic" }}
          allowClose
          onClose={handleOnClose}
        />
      )}

      {isBopInvite && (
        <MessageAlert
          success={true}
          topOffset={100}
          text={inviteMsg?.message}
          textStyle={{ fontStyle: "italic" }}
          allowClose
          onClose={() => setBopInvite(false)}
        />
      )}

      <PMFlow
        selectedBuilding={selectedBuilding}
        onClose={() => {
          setSelectedBuilding(null);
          onReload();
        }}
      />
    </Box>
  );
};
